/**
 * What the organization's Users page and its panel share: how a role reads,
 * and which groups each person is in.
 */
import type { Group, GroupMember, OrganizationRole } from '@optum/api-client/types';

/** A membership role as the console names it. */
export const ROLE_LABEL: Record<OrganizationRole, string> = {
  'platform-client-admin': 'Client admin',
  'platform-client-user': 'Client user',
};

/** Two letters for a person's avatar, from the address before the @. */
export function initialsOf(email: string): string {
  const name = email.split('@')[0] ?? email;
  const parts = name.split(/[._-]+/).filter(Boolean);
  const first = parts[0]?.[0] ?? name[0] ?? '?';
  const second = parts[1]?.[0] ?? '';
  return `${first}${second}`.toUpperCase();
}

/**
 * Group names per platform user id, read from each group's member list.
 * `members[i]` belongs to `groups[i]`; a person in no group has no entry.
 */
export function groupsByUser(
  groups: readonly Group[],
  members: readonly (readonly GroupMember[])[],
): Map<string, string[]> {
  const byUser = new Map<string, string[]>();
  groups.forEach((group, i) => {
    for (const member of members[i] ?? []) {
      const names = byUser.get(member.platform_user_id) ?? [];
      names.push(group.name);
      byUser.set(member.platform_user_id, names);
    }
  });
  for (const names of byUser.values()) names.sort();
  return byUser;
}
